<!-- One template for the three methods; config says what each has. A signed method (Join/Update,
     Access/Update) has the key selector, the Security section, How to sign and the code; SSO has
     the project's Influencer SSO settings instead, as the sign-in provider proves who the person
     is. Join/Update and SSO add the campaign targeting rows. Info lines use the design system's
     field description class. -->
<div class="influencer-auto-join-tab" [class.influencer-auto-join-tab--locked]="locked">
  <div class="mb-4">
    <app-sb-input-checkbox
      mode="switch"
      [label]="config.enableLabel"
      [description]="enableDescription"
      [ngModel]="methodEnabled"
      (ngModelChange)="methodEnabled = $event"
    ></app-sb-input-checkbox>
  </div>

  <div class="sb-field__description mb-3">{{config.intro}}</div>

  <ng-container *ngIf="method === 'sso'">
    <h5 class="mt-4 mb-2">How SSO works</h5>
    <ol class="text-muted mb-4">
      <li *ngFor="let step of ssoSteps">{{step}}</li>
    </ol>

    <h5 class="mb-2">Influencer SSO settings</h5>
    <div *ngIf="ssoState === 'ready'" class="table-responsive">
      <table class="table table-sm access-sso-settings">
        <thead>
          <tr>
            <th scope="col">Name</th>
            <th scope="col">Protocol</th>
            <th scope="col">Status</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let setting of settings">
            <td>{{setting.name}}</td>
            <td>{{protocolLabel(setting)}}</td>
            <td>{{setting.enabled ? 'Enabled' : 'Disabled'}}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div *ngIf="ssoState === 'none'" class="alert alert-info">{{ssoNone}}</div>
    <div *ngIf="ssoWarning" class="alert alert-warning">{{ssoWarning}}</div>
    <app-sb-button
      *ngIf="ssoButtonLabel"
      variant="outline"
      icon="fa-solid fa-id-card"
      routerLink="/influencer-sso"
      [label]="ssoButtonLabel"
      [queryParams]="ssoListQuery"
    ></app-sb-button>
  </ng-container>

  <ng-container *ngIf="linkShown">
    <div class="row mt-4" *ngIf="method === 'sso'">
      <div class="col-lg-6">
        <app-sb-input-select
          label="SSO setting"
          placeholder="Select an SSO setting"
          [clearable]="false"
          [disabled]="locked"
          [items]="settingOptions"
          [ngModel]="selectedSettingId"
          (ngModelChange)="onSettingSelected($event)"
        ></app-sb-input-select>
      </div>
    </div>

    <div *ngIf="defaultCampaignMissing" class="alert alert-warning" [class.mt-4]="method === 'sso'">
      The default widget campaign is not set or no longer exists, so links without a campaign code will not open. Select one in the widget settings.
    </div>

    <!-- The campaign in the link's path; left empty, the link opens the default widget campaign. -->
    <div class="mb-3" [class.mt-3]="method === 'sso'">
      <app-creatable-select
        labelText="Campaign"
        [hideInfo]="true"
        [isCreatable]="false"
        [isDisabled]="locked"
        [onlyBottomBorder]="false"
        [placeholder]="campaignPlaceholder"
        [selectedValue]="sampleCampaignId"
        [selectOptions]="campaignOptions"
        (optionSelected)="onSampleCampaignChange($event)"
      ></app-creatable-select>
    </div>

    <div *ngIf="config.keyKinds.length" class="mb-3">
      <app-sb-input-radio
        label="Key"
        layout="horizontal"
        ariaLabel="Key"
        [description]="keyDescription"
        [options]="keyOptions"
        [disabled]="locked"
        [ngModel]="keyKind"
        (ngModelChange)="onKeyKindChange($event)"
      ></app-sb-input-radio>
    </div>

    <div class="row" *ngIf="selectedSetting">
      <div class="col-lg-6">
        <app-sb-copy-field
          label="Callback URL"
          help="Register this URL with the identity provider."
          [value]="selectedSetting.callback_url"
        ></app-sb-copy-field>
      </div>
      <div class="col-lg-6" *ngIf="selectedSetting.protocol === 'saml' && selectedSetting.metadata_url">
        <app-sb-copy-field
          label="SAML metadata URL"
          [value]="selectedSetting.metadata_url"
        ></app-sb-copy-field>
      </div>
    </div>

    <div [class.mt-4]="method !== 'email'">
      <app-sb-copy-field
        [label]="config.linkLabel"
        [description]="linkDescription"
        [value]="linkUrl"
      ></app-sb-copy-field>
    </div>
    <div *ngIf="signingFailed" class="bar error-brd sb-field__error">
      <div class="invalid-text">This browser cannot sign links on this page. Open the panel over https to see a signed sample.</div>
    </div>

    <ul *ngIf="method === 'email'" class="mt-3 mb-4 text-muted">
      <li>Replace {{sampleKey}} with the user's {{keyName}}<ng-container *ngIf="tokenPerEmail">, and sign each link for that {{keyWord}} as described under How to sign</ng-container>.</li>
      <li *ngIf="keyKind === 'platformId'">Set influencer_type to the user's influencer type, here the campaign's first segment's. The ID is signed with it, and the link opens that type's segment of the campaign.</li>
      <li>The user should already be part of the campaign the link opens.</li>
      <li>A link for an unknown email or ID is refused. To show a default share page instead, add an influencer with the email defaultshare&#64;noemail.com, of the campaign's influencer type, and turn on the campaign's Generic Widget.</li>
    </ul>

    <!-- While the method is off the block looks inactive, its controls disabled as the rest of the tab's. -->
    <div class="access-fields">
      <h5 class="mt-4 mb-2">Fields</h5>
      <div class="access-fields__info mb-3">
        <div class="sb-field__description">
          Tick a parameter to add it, with its sample value, to the {{config.linkLabel}}<ng-container *ngIf="config.signs"> and to the signing code</ng-container>.
          <ng-container *ngIf="!config.signs">With SSO the person's identity comes from the sign-in provider, so only the parameters SSO passes on are listed.</ng-container>
          <ng-container *ngIf="signOptionAdds">The sign option under Security adds {{signOptionAdds}} to it.</ng-container>
        </div>
        <div *ngIf="config.savedFields" class="sb-field__description">{{config.savedFields}}</div>
        <div *ngIf="config.editModes" class="sb-field__description">{{config.editModes}}</div>
      </div>
      <!-- The drop-downs attach their panels to the body, so this scrolling wrapper cannot clip them. -->
      <div class="table-responsive">
        <table class="table table-sm access-fields-table">
          <thead>
            <tr>
              <th scope="col">Field</th>
              <th scope="col">Sample value</th>
              <th scope="col">Description</th>
              <th scope="col">URL parameter</th>
            </tr>
          </thead>
          <tbody>
            <ng-container *ngFor="let field of visibleFieldOptions">
              <tr>
                <td class="access-fields-table__field">
                  <div class="access-fields-table__name">
                    <app-sb-input-checkbox
                      [id]="method + '-field-' + field.key"
                      [label]="field.label"
                      [disabled]="locked"
                      [ngModel]="fields[field.key].activated"
                      (ngModelChange)="onFieldToggle(field.key, $event)"
                    ></app-sb-input-checkbox>
                    <span *ngIf="field.neededForNew" class="badge badge-light">Needed for new people</span>
                    <span *ngIf="field.newPeopleOnly" class="badge badge-light">New people only</span>
                    <span *ngIf="field.displayOnly" class="badge badge-light">Not saved</span>
                  </div>
                </td>
                <td><code class="access-fields-table__sample">{{field.sample}}</code></td>
                <td class="access-fields-table__description">{{field.description}}</td>
                <td><code class="access-fields-table__key">{{field.key}}</code></td>
              </tr>
              <tr *ngIf="keyNoteAfter(field.key) as note" class="access-fields-table__note">
                <td colspan="4">{{note}}</td>
              </tr>
            </ng-container>
            <ng-container *ngIf="config.targeting">
              <tr class="access-fields-table__group">
                <td colspan="4">
                  Campaign targeting: left empty, a drop-down says what it uses instead. A segment of the campaign above replaces the influencer type.
                </td>
              </tr>
              <tr *ngFor="let target of targetRows">
                <td class="access-fields-table__field">
                  <app-sb-input-checkbox
                    [id]="method + '-field-' + target.key"
                    [label]="target.label"
                    [ngModel]="fields[target.key].activated"
                    [disabled]="locked || targetBlocked(target.key)"
                    (ngModelChange)="onTargetToggle(target.key, $event)"
                  ></app-sb-input-checkbox>
                </td>
                <td class="access-fields-table__select">
                  <app-creatable-select
                    appendTo="body"
                    [isDisabled]="locked || targetBlocked(target.key) || !fields[target.key].activated"
                    [selectedValue]="fields[target.key].value"
                    [hideLabel]="true"
                    [isCreatable]="false"
                    [onlyBottomBorder]="false"
                    [placeholder]="targetPlaceholder(target.key)"
                    [selectOptions]="targetOptions(target.key)"
                    (optionSelected)="setFieldValue(target.key, $event)"
                  ></app-creatable-select>
                </td>
                <td class="access-fields-table__description">{{targetDescription(target)}}</td>
                <td><code class="access-fields-table__key">{{target.key}}</code></td>
              </tr>
            </ng-container>
          </tbody>
        </table>
      </div>
    </div>

    <ng-container *ngIf="config.signs">
      <hr class="my-4">

      <app-share-widget-access-security
        [project]="project"
        [method]="method"
        [locked]="locked"
        [saving]="saving"
        (stateChange)="onSecurityChange($event)"
      ></app-share-widget-access-security>

      <hr class="my-4">
      <h5 class="mb-3">{{howTo.heading}}</h5>
      <div *ngIf="howTo.intro" class="sb-field__description mb-3">{{howTo.intro}}</div>
      <pre *ngIf="signMode === 'request_sign'" class="access-signing-formula">{{signingFormula}}</pre>
      <ul *ngIf="howTo.points.length" class="text-muted">
        <li *ngFor="let point of howTo.points">{{point}}</li>
      </ul>
      <!-- One Node.js and one PHP field, showing the code that builds the link or the code that POSTs it. -->
      <div class="mb-3">
        <app-sb-input-radio
          label="Code"
          layout="horizontal"
          ariaLabel="Code"
          [options]="codeOptions"
          [disabled]="locked"
          [ngModel]="codeKind"
          (ngModelChange)="onCodeKindChange($event)"
        ></app-sb-input-radio>
      </div>
      <app-sb-copy-field
        class="access-code"
        label="Node.js"
        [multiline]="true"
        [rows]="codeRows(shownCode.node)"
        [value]="shownCode.node"
      ></app-sb-copy-field>
      <app-sb-copy-field
        class="access-code d-block mt-3"
        label="PHP"
        [multiline]="true"
        [rows]="codeRows(shownCode.php)"
        [value]="shownCode.php"
      ></app-sb-copy-field>
      <div *ngFor="let line of config.usage" class="sb-field__description mt-3">{{line}}</div>
    </ng-container>
  </ng-container>

  <!-- Last in the tab, so it stays pinned above the app footer while the whole tab scrolls. It is
       always there, so a switch can be saved on an SSO tab without settings too. -->
  <div class="sb-sticky-actions mt-4">
    <app-sb-button
      variant="primary"
      icon="fa fa-check"
      label="Save"
      [loading]="saving"
      [disabled]="saving"
      (clicked)="save()"
    ></app-sb-button>
  </div>
</div>
